import React from 'react';
import Modal from '../../../components/modals/Modal';
import './AddOrderModal.scss';
import {
  BundleJson,
  Instruction,
  InstructionType,
} from '../../../types/bundleTypes';
import Fetch from '../../../utils/fetch';
import { settings } from '../../../settings';
import Alert from '../../../components/alerts/Alert';
import { Order, OrderStatus } from '../../../types/orderTypes';
import { Loader } from '../../../components/Loader/Loader';
import { classToPlain } from 'class-transformer';
import dayjs from 'dayjs';

interface State {
  orderId?: string;
  order?: Order;
  errorMessage?: string;
  success: boolean;
  searching: boolean;
}

interface Props {
  bundle: BundleJson;
  onCloseModal: () => void;
  onSelectModal: () => void;
}

export default class AddOrderModal extends React.Component<Props, State> {
  private typingTimer: any;

  constructor(props: Props) {
    super(props);

    this.state = {
      orderId: '',
      order: null,
      errorMessage: null,
      success: false,
      searching: false,
    };

    this.onKeyDown = this.onKeyDown.bind(this);
  }

  onKeyDown(event) {
    if (event.keyCode === 27) {
      this.props.onCloseModal();
    } else if (event.keyCode === 13) {
      this.findOrder();
    }
  }

  componentDidMount() {
    document.addEventListener('keydown', this.onKeyDown, false);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKeyDown, false);
  }

  fetchOrder = async (orderId: string): Promise<Order> => {
    try {
      const response = await Fetch.get({
        url: `${settings.newApiUrl}/order/${orderId}`,
      });

      if (!response.ok) {
        return null;
      }
      return await response.json();
    } catch (e) {
      console.warn(`Error fetching order ${orderId}`, e);
    }
    return null;
  };

  findOrder = async () => {
    clearTimeout(this.typingTimer);

    const orderId = this.state.orderId;
    if (!orderId) {
      return;
    }

    if (this.orderIsInBundle(orderId)) {
      this.setState({
        errorMessage: 'The order is already in this bundle',
        order: null,
        searching: false,
      });
      return;
    }

    this.setState({ searching: true });
    const order = await this.fetchOrder(orderId);

    if (order) {
      this.setState({
        errorMessage: null,
        order: order,
        searching: false,
      });
    } else {
      this.setState({
        errorMessage: 'Order not found',
        order: order,
        searching: false,
      });
    }
  };

  orderIsInBundle = (orderId: string): boolean => {
    orderId = orderId.trim();
    return this.props.bundle.instructions
      .map(instr => instr.orderId)
      .includes(orderId);
  };

  updateOrderValue = event => {
    const value = event.target.value;
    if (value.length === 0) {
      this.setState({ orderId: '' });
      return;
    }
    if (Number.isNaN(Number(value))) {
      return;
    }
    this.setState({ orderId: value.toString(), errorMessage: null }, () => {
      if (this.state.orderId.length > 3) {
        clearTimeout(this.typingTimer);
        this.typingTimer = setTimeout(this.findOrder.bind(this), 1000);
      }
    });
  };

  addOrderToBundle = async (): Promise<boolean> => {
    const bundle = this.props.bundle;
    const bundleId = bundle.id;
    const { order } = this.state;

    this.setState({
      errorMessage: '',
    });

    try {
      const newInstructions: Instruction[] = [
        new Instruction(
          order.id,
          InstructionType.Pickup,
          false,
          order.originAddress.fullAddressString,
          null
        ),
        new Instruction(
          order.id,
          InstructionType.Delivery,
          false,
          order.recipientAddress,
          null
        ),
      ];
      const updatedInstructions = bundle.instructions
        .slice()
        .concat(newInstructions);
      const response = await Fetch.put({
        url: `${settings.newApiUrl}/bundle/${bundleId}`,

        body: classToPlain(updatedInstructions),
      });
      if (response.status === 422) {
        const msg = await response.json();
        this.setState({
          errorMessage: msg[0].message,
        });
        return false;
      } else if (!response.ok) {
        this.setState({
          errorMessage: 'The bundle could not be updated',
        });
        return false;
      }
    } catch (e) {
      console.warn(`Error adding order ${order.id} to bundle ${bundleId}`, e);
      this.setState({
        errorMessage: 'The bundle could not be updated',
      });
      return false;
    }

    this.setState({ success: true });

    setTimeout(() => {
      this.props.onSelectModal();
    }, 2000);

    return true;
  };

  renderOrder() {
    const { order } = this.state;
    if (!order) {
      return null;
    }

    return (
      <div className="add-order-modal-content-order">
        <table>
          <thead>
            <tr>
              <th style={{ paddingLeft: 4 }}>Id</th>
              <th>Sender</th>
              <th>Recipient</th>
              <th>Delivery</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="add-order-modal-content-orderid">{order.id}</td>
              <td>
                {order.pickupName}
                <br />
                {order.originAddress.fullAddressString}
              </td>
              <td>
                {order.recipientName}
                <br />
                {order.recipientAddress}
              </td>
              <td className="add-order-modal-content-window">
                {dayjs(order.deliverFrom).format('HH:mm')} -{' '}
                {dayjs(order.deliverUntil).format('HH:mm')}
              </td>
            </tr>
          </tbody>
        </table>
        {OrderStatus.Canceled === order.status && (
          <Alert type="danger" style={{ marginTop: 10 }}>
            The order is canceled
          </Alert>
        )}
        {OrderStatus.Delivered === order.status && (
          <Alert type="warning" style={{ marginTop: 10 }}>
            The order has been delivered
          </Alert>
        )}
      </div>
    );
  }

  render() {
    const { order, searching, errorMessage, orderId, success } = this.state;
    return (
      <Modal
        title={`Add order to bundle ${this.props.bundle.id}`}
        onClose={() => this.props.onCloseModal()}
        buttons={[
          {
            title: 'Add',
            onClick: this.addOrderToBundle.bind(this),
            loading: false,
            disabled: success || !order,
          },
        ]}
      >
        <div className="add-order-modal-content">
          <span className="add-order-modal-desc">Enter Order ID: </span>
          <div style={{ width: 10 }} />
          <input
            style={{ width: 100 }}
            maxLength={10}
            value={orderId}
            onChange={this.updateOrderValue}
            autoFocus={true}
          />
          <div style={{ height: 30 }} />
        </div>
        {!searching && order && this.renderOrder()}
        {searching && !errorMessage && (
          <div className="add-order-modal-loading">
            <Loader type="bounce" />
          </div>
        )}
        {errorMessage && (
          <Alert type="warning" style={{ marginTop: 10 }}>
            {errorMessage}
          </Alert>
        )}
        {success && (
          <Alert type="success" style={{ marginTop: 10 }}>
            {`Order ${order.id} has been added to bundle ${this.props.bundle.id}`}
          </Alert>
        )}
      </Modal>
    );
  }
}
